import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { apiError, refundPayment } from "../../../services/paymentsLedger";
import { formatMoney } from "./shared";

const HOW = {
  stripe: "Refunded on the card (or Apple Pay / Google Pay) by Stripe; the customer sees it within 5 to 10 days.",
  pawapay: "Sent back to the Mobile Money number by pawaPay; the final status appears in the payment details.",
  manual: "This provider is not refunded from here: refund the customer on its dashboard (CinetPay, Paystack), then record it here so the totals stay right.",
};

/**
 * Refund a payment, fully or partly. `info`: { provider, remaining, currency,
 * reasons } from the payment details. The admin's password is asked again.
 */
const RefundModal = ({ payment, info, onClose, onDone }) => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState(String(info.remaining));
  const [reason, setReason] = useState("requested_by_customer");
  const [note, setNote] = useState("");
  const [endSubscription, setEndSubscription] = useState(false);
  const [manual, setManual] = useState(info.provider === "manual");
  const [password, setPassword] = useState("");
  const [sending, setSending] = useState(false);

  const value = Number(String(amount).replace(",", "."));
  const valid = value > 0 && value <= info.remaining && password;
  const isSubscription = ["subscription", "subscription_renewal"].includes(payment.type) && payment.payer.registered;
  const provider = manual ? "manual" : info.provider;

  const submit = async (event) => {
    event.preventDefault();
    if (!valid) return;
    setSending(true);
    try {
      const result = await refundPayment(payment.id, { amount: value, reason, note, endSubscription, manual, password });
      toast.success(
        result.refund.status === "manual"
          ? t("Refund recorded")
          : result.refund.status === "pending"
          ? t("Refund requested: it is being processed")
          : t("Refund done")
      );
      onDone();
    } catch (error) {
      toast.error(apiError(error) || t("Error updating data"));
      setSending(false);
    }
  };

  return (
    <Modal show={true} onHide={onClose} centered dialogClassName="ui-modal">
      <form onSubmit={submit}>
        <Modal.Header>
          <Modal.Title>{t("Refund this payment")}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="refund-form">
          <p className="ui-hint">{t(HOW[provider])}</p>
          <label className="refund-field">
            <span>{t("Amount to refund ({{currency}})", { currency: info.currency })}</span>
            <input className="ui-input" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} aria-label={t("Amount to refund")} />
            <small className="ui-hint">
              {t("Up to {{amount}}", { amount: formatMoney(info.remaining, info.currency) })}
              {value > 0 && value < info.remaining ? ` · ${t("partial refund")}` : ""}
            </small>
          </label>
          <label className="refund-field">
            <span>{t("Reason")}</span>
            <select className="ui-input" value={reason} onChange={(e) => setReason(e.target.value)}>
              {info.reasons.map((item) => (
                <option key={item.value} value={item.value}>
                  {t(item.label)}
                </option>
              ))}
            </select>
          </label>
          <label className="refund-field">
            <span>{t("Note (optional)")}</span>
            <input className="ui-input" value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} />
          </label>
          {isSubscription && (
            <label className="refund-check">
              <input type="checkbox" checked={endSubscription} onChange={(e) => setEndSubscription(e.target.checked)} />
              <span>{t("Also end the subscription now (Premium access removed, no renewal)")}</span>
            </label>
          )}
          {info.provider !== "manual" && (
            <label className="refund-check">
              <input type="checkbox" checked={manual} onChange={(e) => setManual(e.target.checked)} />
              <span>{t("Already refunded elsewhere: only record it")}</span>
            </label>
          )}
          <label className="refund-field">
            <span>{t("Your password")}</span>
            <input className="ui-input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} aria-label={t("Your password")} />
          </label>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="ui-btn ui-btn-secondary" onClick={onClose}>
            {t("Cancel")}
          </button>
          <button type="submit" className="ui-btn ui-btn-danger" disabled={!valid || sending}>
            {provider === "manual"
              ? t("Record the refund")
              : t("Refund {{amount}}", { amount: value > 0 ? formatMoney(value, info.currency) : "" })}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default RefundModal;
